/**
 * The previous versions of something edited (Arthur, 19 October 2026: an edited visit note showed up to four
 * copies). The current version is shown once, by the screen; this answers only the versions BEFORE it, oldest to
 * newest, each with who made it, when and why, and the values it held that the next version changed. Built from
 * the edits the API already answers (each edit's "before" is exactly the earlier version's value), so no new read.
 * A leaf module, tested in web/test/earlier-versions.test.ts.
 */
export interface EditLike {
  version: number;
  /** 'withdrawal' changes no fact and is the current state, never an earlier version. */
  kind?: 'as recorded' | 'edit' | 'withdrawal';
  by: string | null;
  at: string;
  reason: string | null;
  reason_text: string | null;
  /** What else the edit did, in words (a building's old name kept, placements re-labelled). */
  notes?: string[];
  changes: { field: string; label: string; before: string | null; after: string | null; withheld?: boolean }[];
}

export interface EarlierVersion {
  version: number;
  /** How this version came to be: as first recorded, or by an edit (with its who, when and why). */
  made: { kind: 'first'; by: string | null; at: string | null } | { kind: 'edit'; by: string | null; at: string; reason: string | null; reason_text: string | null; notes: string[] };
  /** What it held that the version after it changed: `value` then, `next` after. */
  fields: { field: string; label: string; value: string | null; next: string | null; withheld: boolean }[];
}

const editsOf = (all: EditLike[]) => all.filter((e) => e.kind !== 'withdrawal' && e.kind !== 'as recorded' && e.version > 1)
  .sort((a, b) => a.version - b.version);

/** Every version before the current one, oldest first. `first` is the version as first recorded, when known. */
export function earlierVersions(all: EditLike[], first: { by: string | null; at: string | null } | null = null): EarlierVersion[] {
  const edits = editsOf(all);
  return edits.map((e, j) => {
    const maker = j > 0 ? edits[j - 1]! : null;
    return {
      version: e.version - 1,
      made: maker
        ? { kind: 'edit', by: maker.by, at: maker.at, reason: maker.reason, reason_text: maker.reason_text, notes: maker.notes ?? [] }
        : { kind: 'first', by: first?.by ?? null, at: first?.at ?? null },
      fields: e.changes.map((c) => ({ field: c.field, label: c.label, value: c.before, next: c.after, withheld: c.withheld === true })),
    };
  });
}

/** The edit that made the current version: the "Edited" marker's who, when and why. Null when never edited. */
export function latestEdit(all: EditLike[]): EditLike | null {
  const edits = editsOf(all);
  return edits[edits.length - 1] ?? null;
}
